import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import FormInput from '@enerex/unified-ui-react/form/FormInput';
import LoadingButton from '@enerex/unified-ui-react/LoadingButton';
import LookupSpecialField from './LookupSpecialField';
import CommodityIconField from '../commodities/CommodityIconField';
import BaseUomField from '../units-of-measurement/BaseUomField';
import { sameUomId } from '../units-of-measurement/baseUomOptions';
import ZoneStateField from '../zones/ZoneStateField';
import LookupRelatedField from './LookupRelatedField';
import LookupNullableBoolean from './LookupNullableBoolean';
import { buildLookupValues, buildLookupPayload, validateLookupField, hasExistingLookupId } from './lookupFormValues';
import useShowSnackbar from 'hooks/useShowSnackbar';
import { deleteLookupRecord, getLookupRecord, readField, saveLookupRecord } from '../api';

export { buildLookupValues, buildLookupPayload } from './lookupFormValues';

const LookupRecordModal = ({ definition, fields, operation, onClose, onSuccess }) => {
    const showSnackbar = useShowSnackbar();
    const isDelete = operation.type === 'delete';
    const isEdit = operation.type === 'edit';
    const [loading, setLoading] = useState(isEdit);
    const [loadFailed, setLoadFailed] = useState(false);
    const [loadedRecord, setLoadedRecord] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);
    const busy = useRef(false);
    const { control, handleSubmit, reset, setValue, setError: setFieldError } = useForm({ defaultValues: buildLookupValues(fields) });
    useEffect(() => {
        if (!isEdit) return;
        const controller = new AbortController();
        let current = true;
        setLoading(true); setLoadFailed(false); setError('');
        getLookupRecord(definition.id, operation.record.id, controller.signal).then(record => {
            if (current) {
                setLoadedRecord(record);
                reset(buildLookupValues(fields, record));
            }
        }).catch(reason => { if (current) { setLoadFailed(true); setError(reason.message); } })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; controller.abort(); };
    }, [definition.id, fields, isEdit, operation.record, reset, attempt]);

    const perform = async values => {
        if (busy.current || loading || loadFailed) return;
        if (!isDelete && definition.id === 'units-of-measurement' && isEdit && sameUomId(values.baseUoMId, operation.record.id) && Number(values.baseUoMConversion) !== 1) {
            setFieldError('baseUoMConversion', { message: 'A base unit referencing itself must have conversion 1.' }); return;
        }
        busy.current = true; setSaving(true); setError('');
        try {
            const result = isDelete
                ? await deleteLookupRecord(definition.id, operation.record.id)
                : await saveLookupRecord(definition.id, buildLookupPayload(fields, values), isEdit ? operation.record.id : undefined);
            if (result.message) showSnackbar(result.message, 'success');
            onSuccess();
        } catch (reason) {
            const message = reason.message || (isDelete ? 'Unable to delete the record. Please try again.' : 'Unable to save changes. Please try again.');
            setError(message);
            showSnackbar(message, 'error');
        }
        finally { busy.current = false; setSaving(false); }
    };
    const submit = isDelete ? () => perform() : handleSubmit(perform);
    const close = () => { if (!busy.current) onClose(); };
    const renderField = field => {
        const props = { field, control, disabled: saving };
        if (field.filterByCommodityAndDimension) return <BaseUomField key={field.name} {...props} setValue={setValue} />;
        if (field.type === 'commodity-icon') return <CommodityIconField key={field.name} {...props} />;
        const lockExistingValue = isEdit && hasExistingLookupId(readField(loadedRecord, field.name));
        if (field.type === 'lookup-select') return <LookupRelatedField key={field.name} {...props} setValue={setValue} disabled={saving || (field.lockOnEdit && lockExistingValue)} />;
        if (field.type === 'nullable-boolean') return <LookupNullableBoolean key={field.name} {...props} />;
        if (field.type === 'state-select') return <ZoneStateField key={field.name} control={control} setValue={setValue} disabled={saving || lockExistingValue} />;
        if (['commodity-select', 'color', 'checkbox'].includes(field.type)) return <LookupSpecialField key={field.name} {...props} />;
        return <FormInput key={field.name} name={field.name} label={field.label + (field.required ? ' *' : '')}
            aria-label={field.label} type={field.name === 'baseUoMConversion' || field.type === 'url' ? 'text' : field.type} control={control} disabled={saving} maxLength={field.maxLength}
            {...(field.type === 'url' ? { inputMode: 'url' } : {})}
            {...(field.name === 'baseUoMConversion' ? { inputMode: 'decimal' } : field.type === 'number' ? { min: 1, step: 1 } : {})}
            rules={{ validate: value => validateLookupField(field, value) }} />;
    };
    return <ConfirmationModal show centered scrollable dialogClassName="modal-md" onHide={close} backdrop="static" keyboard={!saving}
        className="modal-level-10 settings-lookup__record-modal"
        enforceFocus={isDelete || !fields.some(field => field.type === 'color')}
        title={(isDelete ? 'Delete ' : isEdit ? 'Edit ' : 'Add ') + definition.singularTitle}
        confirmButtonLabel={isDelete ? 'Delete' : 'Save'} confirmButtonVariant={isDelete ? 'danger' : 'primary'}
        isLoading={saving || loading} onConfirm={submit}>
        {error && <p role="alert">{error}</p>}
        {loadFailed && <LoadingButton type="button" onClick={() => setAttempt(value => value + 1)}>Retry loading record</LoadingButton>}
        {loading ? <p role="status">Loading record details...</p> : isDelete ?
            <p>Delete {operation.record.name || operation.record.code || definition.singularTitle.toLowerCase()}? This action cannot be undone.</p> : !loadFailed &&
            <form className="settings-lookup__record-form" onSubmit={submit}>
                {fields.filter(field => field.type !== 'hidden' && !field.hidden && !(isEdit && field.hideOnEdit)).map(renderField)}
            </form>}
    </ConfirmationModal>;
};
export default LookupRecordModal;
